import { apiSlice } from "./apiSlice";
import type { RetrievalGame } from "./quizRetrieval.types";

export interface QuizContext {
  origin: string | null;
  trimester: string | null;
  grade: string | null;
  subject: string | null;
  week: string | null;
  lesson: string | null;
}

export interface QuizCard {
  quiz_id: number;
  title: string;
  contexts: QuizContext[];
  matching_contexts: QuizContext[];
  warnings: string[];
}

export interface QuizMediaReference {
  source: string;
  status: "available" | "unavailable";
  url: string | null;
  diagnostic: string | null;
}

export interface QuizDiagnostic {
  code: string;
  index: number | null;
}

export interface QuizNormalizedGame {
  index: number;
  game: RetrievalGame;
}

export interface QuizDetail {
  quiz_id: number;
  title: string;
  contexts: QuizContext[];
  warnings: string[];
  content_status: string;
  raw_games: unknown;
  normalized_games: QuizNormalizedGame[] | null;
  diagnostics: QuizDiagnostic[];
  media: QuizMediaReference[];
}

export interface QuizSearchFilters {
  id?: number;
  title?: string;
  trimester?: "T1" | "T2" | "T3";
  grade?: string;
  subject?: string;
  week?: number;
  weekFrom?: number;
  weekTo?: number;
  lesson?: string;
}

export interface QuizNextAction {
  cursor: string;
  filters: QuizSearchFilters;
}

export interface QuizActions {
  next: QuizNextAction | null;
  details: number[];
}

export interface QuizChatResponse {
  model_text: string | null;
  cards: QuizCard[];
  details: QuizDetail[];
  actions: QuizActions;
  warnings: string[];
}

export interface QuizSearchActions {
  next: QuizNextAction | null;
}

export interface QuizSearchResponse {
  items: QuizCard[];
  total?: number;
  next_cursor: string | null;
  terminal: boolean;
  warnings: string[];
  actions?: QuizSearchActions;
}

export interface QuizSearchRequest extends QuizNextAction {}

export const quizChatApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    chatQuizRetrieval: builder.mutation<QuizChatResponse, { prompt: string }>({
      query: (data) => ({
        url: "/admin/quiz-retrieval/chat",
        method: "POST",
        data,
      }),
    }),
    searchQuizRetrieval: builder.mutation<
      QuizSearchResponse,
      QuizSearchRequest
    >({
      query: ({ cursor, filters }) => ({
        url: "/admin/quiz-retrieval/search",
        method: "POST",
        data: { cursor, ...filters },
      }),
    }),
    getQuizRetrievalDetail: builder.query<QuizDetail, number>({
      query: (id) => ({
        url: `/admin/quiz-retrieval/quizzes/${id}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useChatQuizRetrievalMutation,
  useSearchQuizRetrievalMutation,
  useLazyGetQuizRetrievalDetailQuery: useGetQuizRetrievalDetailLazyQuery,
} = quizChatApi;
